/* eslint-disable */
import React from "react";
import {
  Card,
  Group,
  Stack,
  Text,
  Avatar,
  Badge,
  Button,
  Divider,
  Box,
  createStyles,
  ThemeIcon,
  ScrollArea,
  Center,
  TextInput,
  Accordion,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconCalendar,
  IconClock,
  IconRefresh,
  IconUser,
} from "@tabler/icons-react";
import { useGetAttendanceAdminV2 } from "api/attendance/attendanceDashboard";
import {
  formatToLocalTime,
  formatToLocalTimeDashboard,
} from "utils/dateFormate";
import { useNavigate } from "react-router-dom";

const useStyles = createStyles((theme) => ({
  container: {
    height: "100%",
    // minHeight: "50vh",
    // maxHeight: "50vh",
    background:
      theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
    borderRadius: "10px",
    border: `1px solid ${theme.colors.gray[2]}`,
    padding: "16px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  title: {
    fontWeight: 700,
    fontSize: "13px",
  },
  controls: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "8px",
  },
  item: {
    border: `1px dashed ${theme.colors.gray[3]}`,
    borderRadius: "8px",
    padding: "5px",
    background: theme.white,
    marginTop: "5px",
  },
  itemRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  },
  userInfo: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
  nameRole: {
    display: "flex",
    flexDirection: "column",
  },
  name: {
    fontWeight: 700,
    fontSize: "13px",
  },
  role: {
    color: theme.colors.gray[6],
    fontSize: "13px",
    marginTop: "2px",
  },
  timePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 10px",
    borderRadius: "8px",
    fontWeight: 700,
    fontSize: "13px",
  },
  green: {
    backgroundColor: theme.colors.green[6],
    color: theme.white,
  },
  red: {
    backgroundColor: theme.colors.red[6],
    color: theme.white,
  },
  details: {
    marginTop: "10px",
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: "8px",
    padding: "10px",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "8px",
  },
  legend: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  legendDot: {
    width: "8px",
    height: "8px",
    borderRadius: "8px",
  },
  viewAll: {
    marginTop: "8px",
  },
  sectionTitle: {
    fontWeight: 700,
    marginTop: "6px",
    marginBottom: "6px",
    color: theme.colors.gray[7],
  },
}));

const formatTime = (iso?: string) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Karachi",
    });
  } catch {
    return iso;
  }
};

const formatDate = (dateKey: string) => {
  try {
    const d = new Date(dateKey);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Karachi",
    });
  } catch {
    return dateKey;
  }
};

const statusColor = (status?: string) => {
  if (!status) return "gray";
  if (status.toLowerCase() === "present") return "green";
  if (status.toLowerCase() === "absent") return "red";
  if (status.toLowerCase() === "half") return "yellow";
  return "blue";
};

export default function ClockInOut() {
  const navigate = useNavigate();

  const today = new Date().toISOString().slice(0, 10);

  const form = useForm({
    initialValues: {
      date: today,
    },
  });

  const selectedDate = form.values.date;

  const { data, isLoading, isError } = useGetAttendanceAdminV2({
    type: "date",
    date: selectedDate,
  });

  const { classes, cx } = useStyles();

  const records = data?.data?.attendanceData ?? [];

  return (
    <Card className={classes.container} shadow="sm">
      <div className={classes.header}>
        <div>
          <Title order={3} style={{ fontSize: "18px" }}>
            Clock-In/Out
          </Title>
        </div>

        <div className={classes.controls}>
          <TextInput
            withAsterisk
            type="date"
            // label="Date"
            min="2000-01-01"
            max={today}
            {...form.getInputProps("date")}
            style={{ minWidth: 160 }}
          />
        </div>
      </div>

      <ScrollArea style={{ height: 320, marginTop: 8 }} type="auto">
        {isLoading && (
          <Center>
            <Text>Loading...</Text>
          </Center>
        )}

        {isError && (
          <Center>
            <Text color="red">Failed to load attendance</Text>
          </Center>
        )}

        {records.length === 0 && !isLoading && (
          <Text size="sm" color="dimmed">
            No attendance records found.
          </Text>
        )}

        {records.map((rec) => {
          const attendanceEntries = Object.entries(rec.attendance ?? {});

          return attendanceEntries.map(([dateKey, dayAttendance]) => {
            const activities = dayAttendance?.activities ?? [];

            const firstActivity = activities[0];

            const clockIn = firstActivity?.punchInTime ?? null;
            const clockOut = firstActivity?.punchOutTime ?? null;

            const pillClass =
              dayAttendance?.status === "present" ? classes.green : classes.red;

            return (
              <Box
                key={`${rec.employee.id}-${dateKey}`}
                className={classes.item}
              >
                <Accordion variant="separated">
                  <Accordion.Item
                    key={`item-${rec.employee.id}-${dateKey}`}
                    value={`${rec.employee.id}-${dateKey}`}
                  >
                    <Accordion.Control>
                      <div className={classes.itemRow}>
                        <div className={classes.userInfo}>
                          {/* <Avatar radius="xl" size="md" color="gray">
                            <IconUser size={18} />
                          </Avatar> */}

                          <div className={classes.nameRole}>
                            <Text className={classes.name}>
                              {rec.employee.name}
                            </Text>
                            <Text size="sm" color="dimmed">
                              {formatDate(dateKey)}
                            </Text>
                          </div>
                        </div>

                        <Stack
                          spacing={6}
                          align="flex-end"
                          style={{
                            alignItems: "flex-end",
                            marginRight: "6px",
                          }}
                        >
                          {/* <ThemeIcon variant="light" size="sm">
                            <IconRefresh size={16} />
                          </ThemeIcon> */}

                          <div className={cx(classes.timePill, pillClass)}>
                            <IconClock size={14} />
                            <span>
                              {clockIn
                                ? formatToLocalTimeDashboard(clockIn)
                                : "—"}{" "}
                              {clockOut
                                ? `– ${formatToLocalTimeDashboard(clockOut)}`
                                : ""}
                            </span>
                          </div>
                        </Stack>
                      </div>
                    </Accordion.Control>

                    {/* Panel: the details (unchanged) */}
                    <Accordion.Panel>
                      <div className={classes.details}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: 8,
                          }}
                        >
                          <div>
                            <Text size="sm" weight={600}>
                              Status
                            </Text>
                            <Badge
                              color={statusColor(dayAttendance?.status)}
                              variant="light"
                              style={{ marginTop: 6 }}
                            >
                              {dayAttendance?.status ?? "—"}
                            </Badge>
                          </div>

                          <div>
                            <Text size="sm" weight={600}>
                              Total Hours
                            </Text>
                            <Text
                              size="xs"
                              style={{ marginTop: 6 }}
                              weight={700}
                            >
                              {typeof dayAttendance?.workingHours === "number"
                                ? `${dayAttendance.workingHours.toFixed(2)} hrs`
                                : "—"}
                            </Text>
                          </div>
                        </div>

                        <Divider />

                        <Text
                          size="sm"
                          weight={700}
                          style={{ marginTop: 8, marginBottom: 8 }}
                        >
                          Activities
                        </Text>

                        <Accordion variant="separated">
                          {activities.length === 0 && (
                            <Text size="xs">
                              No activities recorded for this date.
                            </Text>
                          )}

                          {activities.map((act, idx) => {
                            const label = `${formatToLocalTimeDashboard(
                              act.punchInTime
                            )} → ${
                              act.punchOutTime
                                ? formatToLocalTimeDashboard(act.punchOutTime)
                                : "—"
                            }  ·  ${
                              typeof act.workingHours === "number"
                                ? act.workingHours.toFixed(2) + "h"
                                : "—"
                            }`;

                            return (
                              <Accordion.Item
                                key={`${rec.employee.id}-${dateKey}-act-${idx}`}
                                value={`${rec.employee.id}-${dateKey}-act-${idx}`}
                              >
                                <Accordion.Control>
                                  <div
                                    style={{
                                      display: "flex",
                                      width: "100%",
                                      alignItems: "center", // Align items vertically
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 6,
                                        alignItems: "center",
                                        flexShrink: 0, // Prevent shrinking
                                      }}
                                    >
                                      <div
                                        className={classes.legendDot}
                                        style={{ background: "#45B26B" }}
                                      />
                                      <Text size="sm" weight={600}>
                                        In
                                      </Text>
                                    </div>

                                    <div
                                      style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        flex: 1, // Take up remaining space
                                        marginLeft: 8,
                                        minWidth: 0, // Important for text truncation in flex containers
                                      }}
                                    >
                                      <Text size="sm" weight={600}>
                                        {label}
                                      </Text>
                                    </div>
                                  </div>
                                </Accordion.Control>

                                <Accordion.Panel>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: 8,
                                    }}
                                  >
                                    <div>
                                      <Text size="xs" weight={600}>
                                        Clock In
                                      </Text>
                                      <Text size="xs" style={{ marginTop: 6 }}>
                                        {formatToLocalTime(act.punchInTime)}
                                      </Text>
                                    </div>

                                    <div>
                                      <Text size="xs" weight={600}>
                                        Clock Out
                                      </Text>
                                      <Text size="xs" style={{ marginTop: 6 }}>
                                        {act.punchOutTime
                                          ? formatToLocalTime(act.punchOutTime)
                                          : "—"}
                                      </Text>
                                    </div>

                                    <div style={{ textAlign: "right" }}>
                                      <Text size="xs" weight={600}>
                                        Hours
                                      </Text>
                                      <Text size="xs" style={{ marginTop: 6 }}>
                                        {typeof act.workingHours === "number"
                                          ? act.workingHours.toFixed(2)
                                          : "—"}
                                      </Text>
                                    </div>
                                  </div>
                                </Accordion.Panel>
                              </Accordion.Item>
                            );
                          })}
                        </Accordion>
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              </Box>
            );
          });
        })}
      </ScrollArea>
      <div className={classes.viewAll}>
        <Button
          fullWidth
          variant="light"
          onClick={() => navigate("/attendance-employee")}
        >
          View All Attendance
        </Button>
      </div>
    </Card>
  );
}
